import React, { useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useForm } from "react-hook-form";
import { errorMsg, success } from "../../../utils/notifications";
import {
  createVehiclePricing,
  updateVehiclePricing,
} from "../../../api/pricing.api";
import { FiX } from "react-icons/fi";
import { getAllVehicleTypes } from "../../../api/vehicleType.api";
import { getAllMobileLocations } from "../../../api/mobileLocation.api";

const AddVehiclePricing = ({
  open,
  setOpen,
  fetchVehiclePricings,
  editData,
  setEditData,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [typesResponse, locationsResponse] = await Promise.all([
          getAllVehicleTypes(),
          getAllMobileLocations(),
        ]);
        setVehicleTypes(typesResponse.data || []);
        setLocations(locationsResponse.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (editData) {
      reset({
        vehicleType: editData.vehicleType._id,
        location: editData.location._id,
        basePricePerWeek: editData.basePricePerWeek,
        isActive: editData.isActive,
      });
    } else {
      reset({
        vehicleType: "",
        location: "",
        basePricePerWeek: "",
        isActive: true,
      });
    }
  }, [editData, reset, open]);

  const onSubmit = async (data) => {
    try {
      if (editData) {
        await updateVehiclePricing(editData._id, data);
        success("Success!", "Pricing updated successfully!");
      } else {
        await createVehiclePricing(data);
        success("Success!", "Pricing created successfully!");
      }

      // Reset form and close dialog after both create and update
      reset({
        vehicleType: "",
        location: "",
        basePricePerWeek: "",
        isActive: true,
      });
      setOpen(false);
      setEditData(null);
      fetchVehiclePricings();
    } catch (error) {
      errorMsg(
        "Error",
        error.response?.data?.message || "Something went wrong"
      );
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditData(null);
    reset({
      vehicleType: "",
      location: "",
      basePricePerWeek: "",
      isActive: true,
    });
  };
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all w-full max-w-md sm:my-8 sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <FiX className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 w-full text-center sm:mt-0 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold leading-6 text-gray-900"
                    >
                      {editData
                        ? "Edit Vehicle Pricing"
                        : "Add New Vehicle Pricing"}
                    </Dialog.Title>
                    {loading ? (
                      <div className="flex justify-center items-center h-64">
                        <p>Loading data...</p>
                      </div>
                    ) : (
                      <div className="mt-4">
                        <form onSubmit={handleSubmit(onSubmit)}>
                          <div className="space-y-4">
                            <div>
                              <label
                                htmlFor="vehicleType"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Vehicle Type{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="mt-1">
                                <select
                                  id="vehicleType"
                                  {...register("vehicleType", {
                                    required: true,
                                  })}
                                  className={`block w-full rounded-md border shadow-sm focus:border-yellow-500 focus:ring-yellow-500 text-base px-4 py-3 sm:text-sm ${
                                    errors.vehicleType
                                      ? "border-red-500"
                                      : "border-gray-300"
                                  }`}
                                  disabled={editData}
                                >
                                  <option value="">Select Vehicle Type</option>
                                  {vehicleTypes.map((type) => (
                                    <option key={type._id} value={type._id}>
                                      {type.name}
                                    </option>
                                  ))}
                                </select>
                                {errors.vehicleType && (
                                  <p className="mt-1 text-sm text-red-600">
                                    Vehicle Type is required
                                  </p>
                                )}
                              </div>
                            </div>

                            <div>
                              <label
                                htmlFor="location"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Location <span className="text-red-500">*</span>
                              </label>
                              <div className="mt-1">
                                <select
                                  id="location"
                                  {...register("location", { required: true })}
                                  className={`block w-full rounded-md border shadow-sm focus:border-yellow-500 focus:ring-yellow-500 text-base px-4 py-3 sm:text-sm ${
                                    errors.location
                                      ? "border-red-500"
                                      : "border-gray-300"
                                  }`}
                                  disabled={editData}
                                >
                                  <option value="">Select Location</option>
                                  {locations.map((location) => (
                                    <option
                                      key={location._id}
                                      value={location._id}
                                    >
                                      {location.name}
                                    </option>
                                  ))}
                                </select>
                                {errors.location && (
                                  <p className="mt-1 text-sm text-red-600">
                                    Location is required
                                  </p>
                                )}
                              </div>
                            </div>

                            <div>
                              <label
                                htmlFor="basePricePerWeek"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Price Per 10 Vehicles Per Week{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="mt-1">
                                <input
                                  type="number"
                                  id="basePricePerWeek"
                                  {...register("basePricePerWeek", {
                                    required: true,
                                    min: 0,
                                  })}
                                  className={`block w-full rounded-md border shadow-sm focus:border-yellow-500 focus:ring-yellow-500 text-base px-4 py-3 sm:text-sm ${
                                    errors.basePricePerWeek
                                      ? "border-red-500"
                                      : "border-gray-300"
                                  }`}
                                  placeholder="Enter price Per 10 Vehicles Per Week "
                                />
                                {errors.basePricePerWeek && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.basePricePerWeek.type === "required"
                                      ? "Price is required"
                                      : "Price must be positive"}
                                  </p>
                                )}
                              </div>
                            </div>

                            {editData && (
                              <div>
                                <label
                                  htmlFor="isActive"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Status
                                </label>
                                <div className="mt-1">
                                  <select
                                    id="isActive"
                                    {...register("isActive")}
                                    className="block w-full rounded-md px-4 py-3 border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                                  >
                                    <option value={true}>Active</option>
                                    <option value={false}>Inactive</option>
                                  </select>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-3">
                            <button
                              type="button"
                              className="inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto"
                              onClick={() => {
                                setOpen(false);
                                setEditData(null);
                              }}
                              disabled={isSubmitting}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="inline-flex w-full justify-center rounded-md bg-yellow-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 sm:w-auto"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <span className="flex items-center">
                                  <svg
                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  {editData ? "Updating..." : "Saving..."}
                                </span>
                              ) : editData ? (
                                "Update"
                              ) : (
                                "Save"
                              )}
                            </button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddVehiclePricing;
